import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  FilterAndSortComponent,
  HeaderComponent,
  Scan,
  ScanButtonComponent,
  ScannerStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER,
  StepHeaderComponent } from '@softline/application';
import { Store } from '@softline/core';
import { map, skip, Subscription, switchMap } from 'rxjs';
import { ArtikelHeaderComponent } from '../../../components/artikel-header/artikel-header.component';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE } from '../../../kommissionierung.shared';
import { RemoteConfigService } from '../../../services/remote-config.service';
import { LlrBestellungStore } from '../../../store/bestellung.store';
import { LlrKommissionierungStore } from '../../../store/kommissionierung.store';
import { LlrPalleteStore } from '../../../store/palette.store';
import { Palette } from '../../../types/palette';

import { FormatPipe } from '@softline/ui-core';
import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-paletten-auswahl',
  imports: [
    CommonModule,
    FilterAndSortComponent,
    HeaderComponent,
    StepHeaderComponent,
    ScanButtonComponent,
    RouterModule,
    ArtikelHeaderComponent,
    FormatPipe,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './paletten-auswahl.component.html',
  styleUrls: ['./paletten-auswahl.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush })
export class PalettenAuswahlComponent implements OnInit, OnDestroy, BackNavigable {
  private store = inject(Store);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private remoteConfigService = inject(RemoteConfigService);
  private backNavigationService = inject(BackNavigationService);
  private scanSubscription?: Subscription;

  readonly paletteEinzelEingabe = this.remoteConfigService.config.paletteEinzelEingabe;

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            return komm?.bewebskomms[index];
          })
        )
      )
    )
  );

  readonly paletten$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrPalleteStore.getters.all
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_PALETTE,
    LlrBestellungStore.getters.loading
  );


  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: PalettenAuswahlComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_PALETTENDATEN_SCANNEN',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-barcode-scan',
          execute: async () => this.scanPalettendaten() }] });
    const bewe = this.bewegung();

    if (!bewe) return;

    if (
      bewe?.kommissionierDaten?.palette?.id &&
      this.activatedRoute.snapshot.queryParamMap.get('paletteId')?.length === 0
    )
      await this.router.navigate(
        [`llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`],
        {
          queryParams: {
            paletteId: bewe?.kommissionierDaten?.palette?.id } }
      );

    this.scanSubscription = this.store
      .observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest)
      .pipe(skip(1))
      .subscribe(async (scan) => {
        await this.scanPalettendaten(scan);
      });
  }

  async ngOnDestroy(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      PalettenAuswahlComponent
    );

    if (this.scanSubscription && !this.scanSubscription.closed)
      this.scanSubscription.unsubscribe();

    this.backNavigationService.set(undefined);
    this.scanSubscription = undefined;
  }

  async navigateBack(): Promise<void> {
    const bewegung = this.bewegung();

    if (!bewegung) return;

    await this.router.navigate([
      `llr-bskomm/bewegung/${bewegung.id}/palettenart-auswahl`]);
  }

  private async scanPalettendaten(scan?: Scan) {
    try {
      let scanParam;

      if (!scan) {
        scanParam = await this.store.dispatch(
          SOFTLINE_FEATURE_SCANNER,
          ScannerStore.actions.scan,
          {
            labelType: ['ean13', 'ean128', 'code39', 'code128', 'ean8'] }
        );
      } else {
        scanParam = scan;
      }

      if (!scanParam) return;

      const bewe = this.bewegung();

      if (!bewe) return;

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.scanKommissionierungDaten,
        {
          idkopfbskomm: bewe.idkopfbskomm + '',
          idbewebskomm: bewe.id + '',
          type: scanParam.labelType ?? '',
          code: scanParam.data ?? '' }
      );

      if (!result) return;

      if (result.kommissionierDaten?.palette?.id)
        await this.router.navigate(
          [`llr-bskomm/bewegung/${result.id}/artikeldaten-erfassung`],
          {
            queryParams: {
              paletteId: result.kommissionierDaten.palette.id } }
        );

      return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async navigateToNextStepWithSelectedPalette(palette: Palette) {
    try {
      const bewe = this.bewegung();

      if (!bewe) return;

      const resultSchreiben = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.kommissionierungSchreiben,
        {
          bewe,
          params: {
            idkopfbskomm: bewe.idkopfbskomm + '',
            idbewebskomm: bewe.id + '',
            idzielpalette: palette.id + '' } }
      );

      if (!resultSchreiben) return;

      await this.router.navigate(
        [`llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`],
        {
          queryParams: {
            paletteId: palette.id } }
      );
      return;
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  getPaletteArt(palette: Palette) {
    return palette?.palettearten[0];
  }
}
